import React, { useState } from "react";
import { VersaLogo } from "./Logo";
import { Mail, Phone, MapPin, MessageCircle, Linkedin, Instagram, Facebook } from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const FOOTER_COLUMNS = [
  { title: "Products", items: ["Paper Cups", "Tissue Products", "Food Packaging", "Custom Branding"] },
  { title: "Company", items: ["About", "Director's Message", "Sustainability", "Manufacturing", "Industries Served"] },
  { title: "Resources", items: ["Catalogue", "Request Sample", "Request Quote", "FAQ"] },
  { title: "Sustainability Pledge", items: ["FSC-aligned sourcing", "Recyclable products", "Reduced plastic use", "Energy efficient plant"] },
];

const COMPANY = {
  name: "VersaCorp India Private Limited",
  addr: "Plot No-34, KIADB Industrial Area, Kelageri, Dharwad-580009, Karnataka, India",
  email: "info@versacorp.in",
  phone: "+91 7996750556",
  gstin: "29AAJCV7302E1Z9",
  cin: "U17024KA2023PTC180115",
};

const MAPS_EMBED = "https://www.google.com/maps?q=KIADB+Industrial+Area+Kelageri+Dharwad+580009&output=embed";

const INITIAL_FORM = { name: "", email: "", phone: "", subject: "General Enquiry", message: "" };

export const Footer = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success("Thanks! We'll get back within 24 hrs.");
      setForm(INITIAL_FORM);
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer id="contact" data-testid="site-footer" className="bg-[#0A0A0A] text-neutral-300 pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Company info */}
          <div className="lg:col-span-4">
            <VersaLogo size={44} dark />
            <p className="mt-5 text-sm text-neutral-400 leading-relaxed">
              {COMPANY.name} — manufacturer of premium eco-friendly disposable products.
              Trusted by 500+ businesses across India.
            </p>
            <ul className="mt-7 space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-orange-500 shrink-0 mt-0.5" />
                <span>{COMPANY.addr}</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-orange-500" />
                <a href={`mailto:${COMPANY.email}`} className="hover:text-white" data-testid="footer-email">{COMPANY.email}</a>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-orange-500" />
                <a href="tel:+917996750556" className="hover:text-white" data-testid="footer-phone">{COMPANY.phone}</a>
              </li>
              <li className="flex items-center gap-3">
                <MessageCircle size={18} className="text-emerald-400" />
                <a
                  href="https://wa.me/917996750556"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-whatsapp"
                  className="hover:text-white"
                >
                  Chat on WhatsApp
                </a>
              </li>
            </ul>

            <div className="mt-7 pt-6 border-t border-white/10 grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-neutral-500 font-mono-vc uppercase tracking-wider text-[10px]">GSTIN</div>
                <div className="text-neutral-200 font-mono-vc mt-1">{COMPANY.gstin}</div>
              </div>
              <div>
                <div className="text-neutral-500 font-mono-vc uppercase tracking-wider text-[10px]">CIN</div>
                <div className="text-neutral-200 font-mono-vc mt-1">{COMPANY.cin}</div>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="lg:col-span-4">
            <div className="font-display text-lg font-bold text-white mb-4">Find Us</div>
            <div className="rounded-2xl overflow-hidden border border-white/10 aspect-[4/3] bg-neutral-900">
              <iframe
                title="VersaCorp Dharwad Location"
                src={MAPS_EMBED}
                width="100%"
                height="100%"
                style={{ border: 0, filter: "grayscale(0.4) contrast(1.05)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="footer-map"
              />
            </div>
            <div className="mt-5 flex items-center gap-3">
              <Social href="https://wa.me/917996750556" label="WhatsApp" Icon={MessageCircle} />
              <Social href="#" label="LinkedIn" Icon={Linkedin} />
              <Social href="#" label="Instagram" Icon={Instagram} />
              <Social href="#" label="Facebook" Icon={Facebook} />
            </div>
          </div>

          {/* Contact form */}
          <div className="lg:col-span-4">
            <div className="font-display text-lg font-bold text-white mb-4">Send us a message</div>
            <form onSubmit={submit} className="space-y-3" data-testid="footer-contact-form">
              <Input
                placeholder="Your name *"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-white/5 border-white/10 text-white placeholder:text-neutral-500"
                data-testid="footer-form-name"
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  type="email"
                  placeholder="Email *"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-neutral-500"
                  data-testid="footer-form-email"
                />
                <Input
                  placeholder="Phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-neutral-500"
                  data-testid="footer-form-phone"
                />
              </div>
              <Input
                placeholder="Subject"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="bg-white/5 border-white/10 text-white placeholder:text-neutral-500"
                data-testid="footer-form-subject"
              />
              <Textarea
                placeholder="Tell us about your requirement *"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                className="bg-white/5 border-white/10 text-white placeholder:text-neutral-500"
                data-testid="footer-form-message"
              />
              <Button
                type="submit"
                disabled={submitting}
                data-testid="footer-form-submit"
                className="w-full vc-gradient text-white rounded-full h-11 font-semibold border-0"
              >
                {submitting ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </div>
        </div>

        {/* Quick links row */}
        <div className="mt-16 pt-10 border-t border-white/10 grid sm:grid-cols-2 md:grid-cols-4 gap-6 text-sm">
          {FOOTER_COLUMNS.map((c) => (
            <FooterCol key={c.title} title={c.title} items={c.items} />
          ))}
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-neutral-500">
          <div>© {new Date().getFullYear()} VersaCorp India Pvt. Ltd. All rights reserved.</div>
          <div className="font-mono-vc tracking-wider">Made in Dharwad, Karnataka</div>
        </div>
      </div>
    </footer>
  );
};

const Social = ({ href, label, Icon }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    aria-label={label}
    data-testid={`social-${label.toLowerCase()}`}
    className="w-10 h-10 rounded-full border border-white/15 hover:border-white/40 hover:bg-white/5 flex items-center justify-center transition-colors"
  >
    <Icon size={16} />
  </a>
);

const FooterCol = ({ title, items }) => (
  <div>
    <div className="font-display font-bold text-white mb-3">{title}</div>
    <ul className="space-y-2">
      {items.map((i) => (
        <li key={i}>
          <a href="#" className="text-neutral-400 hover:text-white transition-colors">{i}</a>
        </li>
      ))}
    </ul>
  </div>
);
